import { useState, useEffect } from "react";

// Hook to access PM Test Mode state from localStorage
// This allows any component to check if PM test mode is active and get the selected PM

export function usePmTestMode() {
  const [state, setState] = useState(() => {
    const saved = localStorage.getItem("pmTestMode");
    if (!saved) {
      return { isActive: false, pmId: null, pmName: null };
    }
    try {
      const { pmId, pmName } = JSON.parse(saved);
      return { isActive: true, pmId, pmName };
    } catch {
      return { isActive: false, pmId: null, pmName: null };
    }
  });

  // Listen for changes to localStorage (from other components or tabs)
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem("pmTestMode");
      if (!saved) {
        setState({ isActive: false, pmId: null, pmName: null });
      } else {
        try {
          const { pmId, pmName } = JSON.parse(saved);
          setState({ isActive: true, pmId, pmName });
        } catch {
          setState({ isActive: false, pmId: null, pmName: null });
        }
      }
    };

    // Listen for storage events (from other tabs)
    window.addEventListener("storage", handleStorageChange);
    
    // Also check periodically for changes in same tab
    const interval = setInterval(handleStorageChange, 500);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return state;
}

// Helper to check if a program should be visible to the selected PM
// Source of truth is primary_pm_id — it comes from program_assignments, which
// is what the management boards group by. program_manager_id is a legacy cache
// that can point at a previous PM, so it's only checked when there's no
// assignment on the program at all.
export function isProgramVisibleToPm(program, pmId, pmName = null) {
  if (!pmId) return true; // No PM filter active

  // Primary check: primary_pm_id (the actual PM assignment)
  if (program.primary_pm_id === pmId) return true;

  // Legacy fallback: only trust program_manager_id when nothing is assigned
  if (!program.primary_pm_id && program.program_manager_id === pmId) return true;

  // Tertiary checks for other possible fields
  if (program.assigned_pm_id === pmId) return true;
  if (program.pm_user_id === pmId) return true;
  
  // Check scope module assignments
  if (program.scope_modules && Array.isArray(program.scope_modules)) {
    const hasPmAssignment = program.scope_modules.some(
      sm => sm.assigned_pm_user_id === pmId || sm.pm_id === pmId
    );
    if (hasPmAssignment) return true;
  }
  
  return false;
}
